import Phaser from 'phaser';

/**
 * Shield: A destructible barrier made of small blocks.
 * Degrades pixel-by-pixel when hit by projectiles from either side.
 */
export class Shield {
  private scene: Phaser.Scene;
  private blocks: Phaser.Physics.Arcade.Sprite[] = [];
  private x: number;
  private y: number;

  // Shield shape template (1 = filled, 0 = empty)
  private static readonly SHAPE = [
    [0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0],
    [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
    [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
    [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
    [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
    [1, 1, 1, 1, 0, 0, 0, 0, 1, 1, 1, 1],
    [1, 1, 1, 0, 0, 0, 0, 0, 0, 1, 1, 1],
  ];

  constructor(scene: Phaser.Scene, x: number, y: number) {
    this.scene = scene;
    this.x = x;
    this.y = y;
  }

  create(): Phaser.Physics.Arcade.Sprite[] {
    const blockSize = 6;
    const shape = Shield.SHAPE;

    for (let row = 0; row < shape.length; row++) {
      for (let col = 0; col < shape[row].length; col++) {
        if (shape[row][col] === 1) {
          const block = this.scene.physics.add.sprite(
            this.x + col * blockSize,
            this.y + row * blockSize,
            'shield-block'
          );
          block.setImmovable(true);
          this.blocks.push(block);
        }
      }
    }

    return this.blocks;
  }

  getBlocks(): Phaser.Physics.Arcade.Sprite[] {
    return this.blocks;
  }

  /**
   * Apply pre-damage to a shield to simulate wear from previous waves.
   * Randomly removes a percentage of blocks to make the shield weaker.
   * @param damagePercent - Percentage of blocks to remove (0-100)
   */
  preDamage(damagePercent: number): Phaser.Physics.Arcade.Sprite[] {
    const removedBlocks: Phaser.Physics.Arcade.Sprite[] = [];
    if (damagePercent <= 0 || this.blocks.length === 0) return removedBlocks;

    const blocksToRemove = Math.floor(this.blocks.length * (damagePercent / 100));

    // Prefer removing blocks from edges for a natural degradation look
    // Shuffle and pick blocks weighted toward edges
    const blocksByEdgeScore = this.blocks.map(block => {
      // Calculate how "edge-like" this block is (higher = more edge)
      const centerX = this.x + 33; // Approximate center of shield (6px * 12cols / 2)
      const centerY = this.y + 18; // Approximate center of shield (6px * 7rows / 2)
      const distFromCenter = Phaser.Math.Distance.Between(block.x, block.y, centerX, centerY);
      return { block, score: distFromCenter + Math.random() * 15 };
    });

    // Sort by edge score descending (edge blocks first) with randomness
    blocksByEdgeScore.sort((a, b) => b.score - a.score);

    for (let i = 0; i < blocksToRemove && i < blocksByEdgeScore.length; i++) {
      const { block } = blocksByEdgeScore[i];
      block.destroy();
      removedBlocks.push(block);
      const idx = this.blocks.indexOf(block);
      if (idx !== -1) this.blocks.splice(idx, 1);
    }

    return removedBlocks;
  }

  damageAt(x: number, y: number, radius: number = 12): void {
    // Remove blocks within radius of impact point
    this.blocks = this.blocks.filter(block => {
      const dist = Phaser.Math.Distance.Between(block.x, block.y, x, y);
      if (dist < radius) {
        block.destroy();
        return false;
      }
      return true;
    });
  }

  destroy(): void {
    this.blocks.forEach(block => block.destroy());
    this.blocks = [];
  }
}
